/**
 * Inline preview for travel attachments.
 * Images are rendered as thumbnails, other files show an icon + name.
 * Collapsible via a chevron toggle.
 */

import { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  File as FileIcon,
  Paperclip,
} from 'lucide-react';
import type { TravelAttachmentResponse } from '../api';
import api from '@/lib/api';

function getFileIcon(mimeType?: string) {
  if (!mimeType) return FileIcon;
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.startsWith('video/')) return Film;
  if (mimeType.startsWith('audio/')) return Music;
  if (mimeType.startsWith('text/') || mimeType.includes('pdf') || mimeType.includes('document'))
    return FileText;
  return FileIcon;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mimeType?: string): boolean {
  return !!mimeType && mimeType.startsWith('image/');
}

function isText(mimeType?: string): boolean {
  if (!mimeType) return false;
  return (
    mimeType.startsWith('text/') ||
    mimeType === 'application/json' ||
    mimeType === 'application/xml'
  );
}

/** Fetches a presigned download URL and caches it for the session */
const urlCache = new Map<string, { url: string; expires: number }>();

async function getUrl(fileId: string): Promise<string> {
  const cached = urlCache.get(fileId);
  if (cached && cached.expires > Date.now()) return cached.url;
  const { downloadUrl, expiresAt } = await api.getDownloadUrl(fileId);
  urlCache.set(fileId, { url: downloadUrl, expires: new Date(expiresAt).getTime() - 30_000 });
  return downloadUrl;
}

// ---------------------------------------------------------------------------
// Single attachment preview
// ---------------------------------------------------------------------------

function AttachmentItem({ attachment }: { attachment: TravelAttachmentResponse }) {
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (isImage(attachment.mimeType)) {
      getUrl(attachment.fileId)
        .then((url) => {
          if (!cancelled) setImgUrl(url);
        })
        .catch(() => {
          if (!cancelled) setLoadError(true);
        });
    } else if (isText(attachment.mimeType)) {
      getUrl(attachment.fileId)
        .then((url) => fetch(url))
        .then((r) => r.text())
        .then((text) => {
          if (!cancelled) setTextContent(text.slice(0, 500));
        })
        .catch(() => {
          if (!cancelled) setLoadError(true);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [attachment.fileId, attachment.mimeType]);

  const Icon = getFileIcon(attachment.mimeType);

  // Image preview
  if (isImage(attachment.mimeType)) {
    if (loadError) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ImageIcon className="h-3 w-3" />
          <span className="truncate">{attachment.fileName}</span>
        </div>
      );
    }
    return (
      <div className="space-y-1">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={attachment.fileName || 'attachment'}
            className="rounded-md max-h-48 max-w-full object-contain border border-border"
            onError={() => setLoadError(true)}
          />
        ) : (
          <div className="h-24 rounded-md bg-muted/50 animate-pulse flex items-center justify-center">
            <ImageIcon className="h-5 w-5 text-muted-foreground/40" />
          </div>
        )}
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <span className="truncate">{attachment.fileName}</span>
          {attachment.fileSize != null && <span>({formatFileSize(attachment.fileSize)})</span>}
        </div>
      </div>
    );
  }

  // Text preview
  if (isText(attachment.mimeType) && textContent) {
    return (
      <div className="space-y-1">
        <pre className="text-xs bg-muted/50 rounded-md p-2 max-h-32 overflow-auto whitespace-pre-wrap break-words border border-border font-mono">
          {textContent}
          {textContent.length >= 500 ? '...' : ''}
        </pre>
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <FileText className="h-3 w-3" />
          <span className="truncate">{attachment.fileName}</span>
          {attachment.fileSize != null && <span>({formatFileSize(attachment.fileSize)})</span>}
        </div>
      </div>
    );
  }

  // Generic file — icon + name
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{attachment.fileName || attachment.fileId}</span>
      {attachment.fileSize != null && (
        <span className="shrink-0">({formatFileSize(attachment.fileSize)})</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component — collapsible list of previews
// ---------------------------------------------------------------------------

interface TravelAttachmentPreviewProps {
  attachments: TravelAttachmentResponse[];
  /** Render in compact mode (smaller previews, used in calendar cells) */
  compact?: boolean;
}

export function TravelAttachmentPreview({
  attachments,
  compact = false,
}: TravelAttachmentPreviewProps) {
  const [collapsed, setCollapsed] = useState(false);

  if (attachments.length === 0) return null;

  const Chevron = collapsed ? ChevronRight : ChevronDown;

  return (
    <div className="mt-1">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setCollapsed(!collapsed);
        }}
        className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
      >
        <Chevron className="h-3 w-3" />
        <Paperclip className="h-2.5 w-2.5" />
        <span>
          {attachments.length} {attachments.length === 1 ? 'file' : 'files'}
        </span>
      </button>

      {!collapsed && (
        <div className={`mt-1 space-y-1.5 ${compact ? 'max-h-28 overflow-auto' : ''}`}>
          {attachments.map((att) => (
            <AttachmentItem key={att.attachmentId} attachment={att} />
          ))}
        </div>
      )}
    </div>
  );
}
